import { logger } from "@/utils/logger";

import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import FormHeader from './FormHeader';
import FormRenderer from './FormRenderer';
import { useFormContext } from '@/contexts/FormContext';
import { useStateManager } from '@/hooks/useStateManager';
import type { DepartmentHours } from '@/utils/messageParser/types';

interface FormFrameworkProps {
  formType: string;
  formData: { [key: string]: string };
  onUpdateData: (data: { [key: string]: string }) => void;
  onClose: () => void;
  onSubmit?: (formData: { [key: string]: string }) => void;
  isOfferHighlighted?: boolean;
  currentStep?: number;
  onStepChange?: (step: number) => void;
  onCollapse?: (collapsed: boolean) => void;
  openingHours?: DepartmentHours[];
  title?: string; // Allow explicit title override
  initialCollapsed?: boolean; // Start form in collapsed state
}

export interface FormFrameworkRef {
  collapse: () => void;
  expand: () => void;
}

const FormFramework = forwardRef<FormFrameworkRef, FormFrameworkProps>(({
  formType,
  formData,
  onUpdateData,
  onClose,
  onSubmit,
  isOfferHighlighted,
  currentStep = 0,
  onStepChange = () => {},
  onCollapse,
  openingHours,
  title,
  initialCollapsed = false,
}, ref) => {
  logger.log('📋 FormFramework rendering with formType:', formType, 'initialCollapsed:', initialCollapsed);
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [internalStep, setInternalStep] = useState(0);
  const { aiFormData, setUserInteracting } = useFormContext();
  const { state, updateState } = useStateManager();

  // Notify parent of initial collapsed state
  useEffect(() => {
    if (onCollapse && initialCollapsed) {
      onCollapse(true);
    }
  }, []);

  // Track user interaction with the form (must NOT interfere with React onChange)
  useEffect(() => {
    let interactionTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const isFormInputElement = (el: EventTarget | null): el is HTMLElement => {
      if (!el || !(el instanceof HTMLElement)) return false;
      return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT';
    };

    const handleInteractionStart = (e: Event) => {
      if (!isFormInputElement(e.target)) return;

      setUserInteracting(true);

      if (interactionTimeoutId) {
        clearTimeout(interactionTimeoutId);
        interactionTimeoutId = null;
      }
    };

    const handleInteractionEnd = (e: FocusEvent) => {
      if (!isFormInputElement(e.target)) return;

      const relatedTarget = e.relatedTarget as HTMLElement | null;
      const movingToAnotherInput = isFormInputElement(relatedTarget);

      if (!movingToAnotherInput) {
        interactionTimeoutId = setTimeout(() => {
          setUserInteracting(false);
        }, 3000);
      }
    };

    // IMPORTANT:
    // - Do NOT use capture phase here.
    // - A capture-phase native listener can trigger React state updates BEFORE React's
    //   own onChange runs, causing controlled inputs to lose characters.
    document.addEventListener('focusin', handleInteractionStart);
    document.addEventListener('input', handleInteractionStart);
    document.addEventListener('focusout', handleInteractionEnd as EventListener);

    return () => {
      document.removeEventListener('focusin', handleInteractionStart);
      document.removeEventListener('input', handleInteractionStart);
      document.removeEventListener('focusout', handleInteractionEnd as EventListener);

      if (interactionTimeoutId) {
        clearTimeout(interactionTimeoutId);
      }

      setUserInteracting(false);
    };
  }, [setUserInteracting]);

  const handleClose = () => {
    setCollapsed(false);
    onClose();
  };

  const handleToggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    if (onCollapse) {
      onCollapse(newCollapsed);
    }
  };

  const handleCollapseForm = () => {
    setCollapsed(true);
    if (onCollapse) {
      onCollapse(true);
    }
  };

  const handleExpandForm = () => {
    setCollapsed(false);
    if (onCollapse) {
      onCollapse(false);
    }
  };

  const handleStepChange = (step: number) => {
    // FormFramework: handleStepChange called with step
    setInternalStep(step);
    onStepChange(step);
    // FormFramework: Step changed to step
  };

  // Use internal step for step management
  const activeStep = internalStep;

  // Expose collapse and expand functions to parent
  useImperativeHandle(ref, () => ({
    collapse: handleCollapseForm,
    expand: handleExpandForm,
  }), []);

  return (
    <Card className="w-full bg-white/95 backdrop-blur-sm d2cbot-bot_border-0">
      <FormHeader
        formType={formType}
        collapsed={collapsed}
        onToggleCollapse={handleToggleCollapse}
        onClose={handleClose}
        formData={aiFormData || formData} // Pass AI form data if available, fallback to regular formData
        title={title}
      />
      {!collapsed && (
        <CardContent className="p-2">
          <FormRenderer
            formType={formType}
            formData={formData}
            onUpdateData={onUpdateData}
            onSubmit={onSubmit}
            currentStep={activeStep}
            onStepChange={handleStepChange}
            openingHours={openingHours}
            title={title}
          />
        </CardContent>
      )}
    </Card>
  );
});

FormFramework.displayName = 'FormFramework';

export default FormFramework;
